import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ContactWidgetService } from '../../../../services/contact-widget.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModule } from '../../../../../shared/shared.module';
import {
	IContactWidget,
	IManualContactEditData
} from '../../../../interfaces/contact-widget.interface';
import { ManualContact } from '../../../../interfaces/contact.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-manual-contact-edit',
	imports: [
		SharedModule,
	],
  templateUrl: './manual-contact-edit.component.html',
  styleUrl: './manual-contact-edit.component.scss'
})
export class ManualContactEditComponent {
	// Services
	private readonly contactWidgetService: ContactWidgetService = inject(ContactWidgetService);
	private readonly snackBar: MatSnackBar = inject(MatSnackBar);
	protected readonly translate: TranslateService = inject(TranslateService);

	contactName: string = '';
	phoneNumber: string = '';
	contactWidget: IContactWidget;

	constructor(
		public dialogRef: MatDialogRef<ManualContactEditComponent>,
		@Inject(MAT_DIALOG_DATA) public data: IManualContactEditData
	) {
		this.contactName = data.contact.name;
		this.phoneNumber = data.contact.phoneNumber;
		this.contactWidget = data.widget;
	}

	getInitials(name: string): string {
		return name
		.split(' ')
		.map(part => part.charAt(0).toUpperCase())
		.slice(0, 2)
		.join('');
	}

	isFormValid(): boolean {
		return this.contactName.trim().length >= 2 &&
			this.phoneNumber.trim().length > 0;
	}

	hasChanges(): boolean {
		return this.contactName.trim() !== this.data.contact.name ||
			this.phoneNumber.trim() !== this.data.contact.phoneNumber;
	}

	canSave(): boolean {
		return this.isFormValid() && this.hasChanges();
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave(): Promise<void> {
		if (!this.canSave()) {
			return;
		}

		if (!this.contactWidget?._id) {
			console.error('[ManualContactEdit] Cannot save - widget ID missing');
			return;
		}

		try {
			// Create updated contact
			const result = await this.contactWidgetService.updateAstimaxContactManualContact(this.data.contact, {
				name: this.contactName.trim(),
				phoneNumber: this.phoneNumber.trim()
			});

			if (result.success && result?.manualContact) {
				// Update in widget settings through service
				await this.updateManualContactInWidget(result.manualContact);

				// Save widget settings to database
				await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
					this.contactWidget._id,
					this.contactWidget
				);
			}

			// Close dialog with result
			this.dialogRef.close({
				widget: this.contactWidget,
				name: this.contactName.trim(),
				phoneNumber: this.phoneNumber.trim(),
				contact: result.manualContact,
				success: result.success
			});
		} catch (error) {
			console.error('[ManualContactEdit] Error updating contact:', error);
			this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_UPDATE_CONTACT'),
				'Close',
				{ duration: 3000 }
			);
		}
	}

	private async updateManualContactInWidget(updatedContact: ManualContact): Promise<void> {
		if (!this.contactWidget?._id) {
			throw new Error('Widget ID missing');
		}

		// Get current manual contacts from service
		const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
		if (!widgetData) {
			throw new Error('Widget data not found');
		}

		const currentManualContacts = widgetData.manualContacts;
		const updatedManualContacts = currentManualContacts.map((contact: ManualContact) =>
			contact.contactId === this.data.contact.contactId ? updatedContact : contact
		);

		// Update manual contacts through service
		this.contactWidgetService.updateManualContacts(this.contactWidget._id, updatedManualContacts, true);
	}
}
